/**
 * The visualizer is a deliberately dark stage embedded in the light platform
 * shell. One focused surface with one thing moving on it reads as premium and
 * keeps attention on the animation instead of the page chrome around it.
 *
 * Renderers and the player both import from here so a tone means the same
 * colour in the canvas, the pseudocode gutter and the state panel.
 */
import type { Tone } from './types';

export const CANVAS = {
  bg: '#1c1917',
  panel: '#262220',
  panelBorder: '#3a3330',
  cell: '#2d2825',
  cellBorder: '#4b4340',
  text: '#f5f5f4',
  textDim: '#a8a29e',
  textFaint: '#78716c',
} as const;

export const TONES: Record<Tone, string> = {
  accent: '#ff8a4c',
  info: '#60a5fa',
  success: '#34d399',
  warn: '#fbbf24',
  muted: '#78716c',
};

/** Fill used behind a marked cell — the tone at low opacity. */
export const TONE_FILLS: Record<Tone, string> = {
  accent: 'rgba(255, 138, 76, 0.18)',
  info: 'rgba(96, 165, 250, 0.18)',
  success: 'rgba(52, 211, 153, 0.18)',
  warn: 'rgba(251, 191, 36, 0.18)',
  muted: 'rgba(120, 113, 108, 0.18)',
};

export function toneColor(tone: Tone | undefined, fallback: Tone = 'accent'): string {
  return TONES[tone ?? fallback];
}

export function toneFill(tone: Tone | undefined, fallback: Tone = 'accent'): string {
  return TONE_FILLS[tone ?? fallback];
}

export const MONO = "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace";

/** Geometry shared by renderers so views stacked in one step line up. */
export const GEO = {
  cell: 56,
  cellGap: 12,
  nodeRadius: 24,
  levelGap: 76,
  padding: 24,
} as const;
